/**
 * @fileoverview Sales dashboard page.
 * @description The landing page after sign-in, read in one call from GET /sales/dashboard
 * (Confluence 3.5.11.40): a greeting, four summary tiles, six cards that each open their page, and
 * the Needs Attention list. The backend decides the scope from the token and leaves out every
 * section the caller may not see, so the page renders exactly the sections it receives and never
 * derives a figure or checks a role. There are no filters: the figures are always the April to
 * March financial year to date. A failed load shows an error with a retry, never zero figures.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  DashboardAttentionSection,
  DashboardKpiTiles,
  DashboardSkeleton,
  DashboardSummaryCards,
  hasNoSalesRecords,
  PageLoadError,
  useSalesDashboard,
} from '@/features/sales'
import PageHeader from '@/shared/components/molecules/PageHeader'
import StateMessage from '@/shared/components/molecules/StateMessage'

/**
 * @component
 */
function DashboardPage() {
  const { t } = useTranslation()
  const canAddUsers = useCurrentSession().data?.privileges?.includes('users:invite') ?? false
  const { data: dashboard, isPending, isError } = useSalesDashboard()

  const name = dashboard?.greetingName
  const header = (
    <PageHeader
      title={
        name ? t('sales.dashboard.greeting', { name }) : t('sales.dashboard.greetingAnonymous')
      }
      description={t('sales.dashboard.description')}
    />
  )

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <DashboardSkeleton />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <PageLoadError message={t('sales.dashboard.loadError')} testId="dashboard-load-error" />
      </div>
    )
  }

  if (hasNoSalesRecords(dashboard)) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <section className="flex min-h-state items-center justify-center rounded-card border border-chip-border bg-white">
          <StateMessage
            icon="info"
            title={t('sales.dashboard.empty.title')}
            description={t('sales.dashboard.empty.description')}
            testId="dashboard-empty"
          />
        </section>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {header}
      <DashboardKpiTiles dashboard={dashboard} />
      <DashboardSummaryCards dashboard={dashboard} canAddUsers={canAddUsers} />
      {dashboard.needsAttention && (
        <DashboardAttentionSection needsAttention={dashboard.needsAttention} />
      )}
    </div>
  )
}

export default DashboardPage
